/**
 * Pure helpers of the Library (W2-10 projects-data; DESIGN.md §4.1, §5.16).
 *
 *  - view preferences (group by song, sort, grid/list), stored as one JSON object in `settings.ext['library.prefs']`;
 *  - search (every word must match the title, composer or level), sorting and grouping by song (projects that share
 *    a music asset, i.e. the same SHA-256, form one group; a project without a song is a group of its own);
 *  - difficulty labels ("IN Lv.13"), trash retention (30 days), sizes and relative times;
 *  - persistence errors (`PersistError` codes) and the reasons the Library shows for them.
 */

import type { Json } from '../../core/util/types';
import type { AssetId, ProjectId } from '../../core/model/types';
import type { MessageKey } from '../../i18n/types';
import type { ProjectSummary } from '../../persist/types';
import { m } from './library.messages';

// ---------------------------------------------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------------------------------------------

export type LibrarySort = 'edited' | 'opened' | 'created' | 'name' | 'difficulty';
export type LibraryView = 'grid' | 'list';

export interface LibraryPrefs {
  readonly groupBySong: boolean;
  readonly sort: LibrarySort;
  readonly view: LibraryView;
}

export const LIBRARY_SORTS: readonly LibrarySort[] = ['edited', 'opened', 'created', 'name', 'difficulty'];
export const LIBRARY_VIEWS: readonly LibraryView[] = ['grid', 'list'];
export const DEFAULT_PREFS: LibraryPrefs = Object.freeze({ groupBySong: true, sort: 'edited', view: 'list' });

/** settings.ext key of the Library view preferences. */
export const PREFS_KEY = 'library.prefs';

function isSort(v: unknown): v is LibrarySort {
  return typeof v === 'string' && (LIBRARY_SORTS as readonly string[]).includes(v);
}

function isView(v: unknown): v is LibraryView {
  return typeof v === 'string' && (LIBRARY_VIEWS as readonly string[]).includes(v);
}

/** Preferences from stored JSON (unknown or invalid fields fall back to the defaults). */
export function parsePrefs(json: Json | undefined): LibraryPrefs {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return DEFAULT_PREFS;
  const o = json as Readonly<Record<string, Json>>;
  return {
    groupBySong: typeof o.groupBySong === 'boolean' ? o.groupBySong : DEFAULT_PREFS.groupBySong,
    sort: isSort(o.sort) ? o.sort : DEFAULT_PREFS.sort,
    view: isView(o.view) ? o.view : DEFAULT_PREFS.view,
  };
}

export function prefsToJson(p: LibraryPrefs): Json {
  return { groupBySong: p.groupBySong, sort: p.sort, view: p.view };
}

// ---------------------------------------------------------------------------------------------------------------
// Search, sort, groups
// ---------------------------------------------------------------------------------------------------------------

/** Lower-case words of a search query. */
export function queryWords(query: string): readonly string[] {
  return query
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter((w) => w !== '');
}

/** True when every word of the query occurs in the title, composer or level of the project. */
export function matchesQuery(p: ProjectSummary, query: string): boolean {
  const words = queryWords(query);
  if (words.length === 0) return true;
  const hay = `${p.name}\n${p.composer}\n${p.level}`.toLocaleLowerCase();
  return words.every((w) => hay.includes(w));
}

/** Comparator for a sort order (ties broken by name, then id, so the order is stable). */
export function compareProjects(sort: LibrarySort, locale: string): (a: ProjectSummary, b: ProjectSummary) => number {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  const byName = (a: ProjectSummary, b: ProjectSummary): number =>
    collator.compare(a.name, b.name) || collator.compare(a.level, b.level) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  switch (sort) {
    case 'edited':
      return (a, b) => b.updatedAt - a.updatedAt || byName(a, b);
    case 'opened':
      return (a, b) => b.openedAt - a.openedAt || b.updatedAt - a.updatedAt || byName(a, b);
    case 'created':
      return (a, b) => b.createdAt - a.createdAt || byName(a, b);
    case 'name':
      return byName;
    case 'difficulty':
      return (a, b) => b.difficulty - a.difficulty || byName(a, b);
  }
}

export function sortProjects(projects: readonly ProjectSummary[], sort: LibrarySort, locale: string): ProjectSummary[] {
  return [...projects].sort(compareProjects(sort, locale));
}

/** Group key of a project: its song (music asset) or, without a song, the project itself. */
export function songKey(p: ProjectSummary): string {
  return p.musicAsset === null ? `project:${p.id}` : `song:${p.musicAsset}`;
}

export interface SongGroupModel {
  readonly key: string;
  readonly musicAsset: AssetId | null;
  /**
   * The song's title: the name most of its charts use; ties go to the oldest chart (a duplicate "X (copy)" never
   * renames the song of its original, UX-084).
   */
  readonly title: string;
  readonly composer: string;
  /** Charts of the song shown (sorted like the page). */
  readonly projects: readonly ProjectSummary[];
  /** Charts sharing the song in the whole Library (search may show fewer). */
  readonly total: number;
}

function mostCommon(values: readonly string[]): string {
  const counts = new Map<string, number>();
  let best = values[0] ?? '';
  let bestCount = 0;
  for (const v of values) {
    const n = (counts.get(v) ?? 0) + 1;
    counts.set(v, n);
    if (n > bestCount) {
      best = v;
      bestCount = n;
    }
  }
  return best;
}

/** Number of (live) charts per song key, over the whole Library. */
export function songCounts(projects: readonly ProjectSummary[]): ReadonlyMap<string, number> {
  const out = new Map<string, number>();
  for (const p of projects) out.set(songKey(p), (out.get(songKey(p)) ?? 0) + 1);
  return out;
}

/**
 * Groups projects by song. Projects inside a group and the groups themselves follow the sort order (a group sits
 * where its first chart would). `counts` gives the number of charts of each song in the whole Library.
 */
export function groupBySong(
  projects: readonly ProjectSummary[],
  sort: LibrarySort,
  locale: string,
  counts: ReadonlyMap<string, number> = songCounts(projects),
): SongGroupModel[] {
  const sorted = sortProjects(projects, sort, locale);
  const groups = new Map<string, ProjectSummary[]>();
  for (const p of sorted) {
    const key = songKey(p);
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [p]);
    else list.push(p);
  }
  const out: SongGroupModel[] = [];
  for (const [key, list] of groups) {
    const first = list[0];
    if (first === undefined) continue;
    const oldestFirst = [...list].sort((x, y) => x.createdAt - y.createdAt || (x.id < y.id ? -1 : 1));
    out.push({
      key,
      musicAsset: first.musicAsset,
      title: mostCommon(oldestFirst.map((p) => p.name)),
      composer: mostCommon(oldestFirst.map((p) => p.composer).filter((c) => c.trim() !== '')),
      projects: list,
      total: Math.max(counts.get(key) ?? list.length, list.length),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Difficulty labels
// ---------------------------------------------------------------------------------------------------------------

/** Usual Phigros difficulty labels, easiest first. */
export const LEVEL_LABELS = ['EZ', 'HD', 'IN', 'AT', 'SP'] as const;

/** Difficulty number as written in labels: integers without decimals, else one or two decimals. */
export function formatDifficulty(d: number): string {
  if (!Number.isFinite(d)) return '?';
  return String(Math.round(d * 100) / 100);
}

/** "IN Lv.13" (the label alone when it is empty, "Lv.13"). */
export function levelString(label: string, difficulty: number): string {
  const l = label.trim();
  const lv = `Lv.${formatDifficulty(difficulty)}`;
  return l === '' ? lv : `${l} ${lv}`;
}

/** Splits a level display string ("IN Lv.13", "HD  Lv.11.5", "SP Lv.?") into label and number (null if none). */
export function parseLevel(level: string): { readonly label: string; readonly difficulty: number | null } {
  const match = /^(.*?)\s*Lv\.?\s*([0-9]+(?:\.[0-9]+)?)?\s*$/i.exec(level.trim());
  if (match === null) return { label: level.trim(), difficulty: null };
  const num = match[2] === undefined ? null : Number(match[2]);
  return { label: (match[1] ?? '').trim(), difficulty: num !== null && Number.isFinite(num) ? num : null };
}

/** Range of the level number (`meta.difficulty`), as the New chart wizard and Phira's own field (DESIGN.md §5.1). */
export const LEVEL_MIN = 0;
export const LEVEL_MAX = 20;

/**
 * A typed level number ("16.5", "13") in steps of 0.1 (16.55 becomes 16.6); null when empty, not a number or outside
 * 0 to 20.
 */
export function parseLevelNumber(typed: string): number | null {
  const t = typed.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 10) / 10;
  return rounded < LEVEL_MIN || rounded > LEVEL_MAX ? null : rounded;
}

/** The easiest usual label (EZ, HD, IN, AT, SP) the song does not have yet, for the New difficulty dialog. */
export function nextLevelLabel(taken: readonly string[]): string {
  const used = new Set(taken.map((l) => parseLevel(l).label.toUpperCase()));
  for (const l of LEVEL_LABELS) if (!used.has(l)) return l;
  return LEVEL_LABELS[2];
}

// ---------------------------------------------------------------------------------------------------------------
// Trash, sizes, times
// ---------------------------------------------------------------------------------------------------------------

export const DAY_MS = 24 * 60 * 60 * 1000;
/** Projects in the Trash are deleted for good after 30 days (persist purges them). */
export const TRASH_DAYS = 30;

/** Whole days before a trashed project is deleted (0 = today). */
export function trashDaysLeft(trashedAt: number, now: number): number {
  return Math.max(0, Math.ceil((trashedAt + TRASH_DAYS * DAY_MS - now) / DAY_MS));
}

/** Size units (1024-based), as messages so each language writes them its own way. */
const SIZE_UNITS: readonly MessageKey[] = [m.sizeB, m.sizeKB, m.sizeMB, m.sizeGB, m.sizeTB];

/** What formatBytes needs to write a size: the translator and the locale (for the digits). */
export interface SizeText {
  readonly locale: string;
  readonly t: (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;
}

/** "0 B", "12 KB", "3.4 MB", "9.8 GB" (1024-based, locale digits, translated unit). */
export function formatBytes(bytes: number, text: SizeText): string {
  let v = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  let unit = 0;
  while (v >= 1024 && unit < SIZE_UNITS.length - 1) {
    v /= 1024;
    unit++;
  }
  const digits = unit === 0 ? 0 : v < 10 ? 1 : 0;
  const size = new Intl.NumberFormat(text.locale, { maximumFractionDigits: digits }).format(v);
  return text.t(SIZE_UNITS[unit] ?? m.sizeB, { size });
}

/** Locale-grouped integer ("2,341"). */
export function formatCount(n: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
}

/**
 * "2 hours ago", "3 days ago", "last week" (Intl.RelativeTimeFormat); null below one minute (the caller says
 * "just now").
 */
export function relativeTime(at: number, now: number, locale: string): string | null {
  const diff = at - now;
  const abs = Math.abs(diff);
  if (abs < 60_000) return null;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const units: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
    ['minute', 60_000],
    ['hour', 3_600_000],
    ['day', DAY_MS],
    ['week', 7 * DAY_MS],
    ['month', 30 * DAY_MS],
    ['year', 365 * DAY_MS],
  ];
  let chosen: readonly [Intl.RelativeTimeFormatUnit, number] = ['minute', 60_000];
  for (const u of units) if (abs >= u[1]) chosen = u;
  return rtf.format(Math.round(diff / chosen[1]), chosen[0]);
}

/** "IN Lv.15, HD Lv.11 and AT Lv.16" in the locale's words. */
export function joinList(locale: string, items: readonly string[]): string {
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/** "5 Oct 2026, 14:03" in the locale. */
export function formatDateTime(at: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(at);
}

/** "2026-10-05" (local date) for file names. */
export function dateStamp(d: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A file-name-safe version of a project name ("Kyou no Hi [IN Lv.15]"). */
export function fileNameOf(name: string, level: string): string {
  const base = `${name.trim() === '' ? 'chart' : name.trim()}${level.trim() === '' ? '' : ` [${level.trim()}]`}`;
  let out = '';
  for (const ch of base) {
    const c = ch.codePointAt(0) ?? 0;
    out += c < 0x20 || '/\\:*?"<>|'.includes(ch) ? '_' : ch;
  }
  return out.slice(0, 120);
}

// ---------------------------------------------------------------------------------------------------------------
// Persistence errors
// ---------------------------------------------------------------------------------------------------------------

export type PersistCode = 'lockedElsewhere' | 'lockLost' | 'notFound' | 'quota' | 'io';

const PERSIST_CODES: readonly string[] = ['lockedElsewhere', 'lockLost', 'notFound', 'quota', 'io'];

/** The PersistError code of a rejection (Error subclasses and plain objects), or null. */
export function persistCode(err: unknown): PersistCode | null {
  if (typeof err !== 'object' || err === null) return null;
  const code = (err as { readonly code?: unknown }).code;
  return typeof code === 'string' && PERSIST_CODES.includes(code) ? (code as PersistCode) : null;
}

export function isLockedElsewhere(err: unknown): boolean {
  return persistCode(err) === 'lockedElsewhere';
}

/** Message of a rejection, for "details" lines. */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const msg = (err as { readonly message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return String(err);
}

/** Projects by id. */
export function byId(projects: readonly ProjectSummary[]): ReadonlyMap<ProjectId, ProjectSummary> {
  return new Map(projects.map((p) => [p.id, p]));
}

/**
 * Where the keyboard goes when `removed` left a list: the next item of the old order that is still shown, else the
 * nearest one before it (the end of the list), else null (the list is empty or `removed` was not in it).
 */
export function successorOf<T>(before: readonly T[], after: readonly T[], removed: T): T | null {
  const i = before.indexOf(removed);
  if (i < 0) return null;
  const shown = new Set(after);
  for (let k = i + 1; k < before.length; k++) {
    const id = before[k] as T;
    if (shown.has(id)) return id;
  }
  for (let k = i - 1; k >= 0; k--) {
    const id = before[k] as T;
    if (shown.has(id)) return id;
  }
  return null;
}
